// Flat piece silhouettes in a 100 by 100 box. One outline per piece, a copy
// of it behind, and a few details on top. The two color looks hide the copy.
// The mono look draws it as a halo, so the class keeps the name `glyph__shadow`.

import type { CasterId } from '../content/casters';
import type { Owner, PieceKind } from '../engine/types';
import { svg } from '../lib/svg';
import { casterPawnWear } from './casterPawn';
import { CASTER_PAWN_TOP, casterPawnOutline } from './casterPawnShapes';
import { energyStarPath } from './energyStar';

export const GLYPH_BOX = 100;

const SILHOUETTES: Record<PieceKind, string> = {
  pawn: 'M30 86 L70 86 L70 78 L62 72 L58 48 A14 14 0 1 0 42 48 L38 72 L30 78 Z',
  rook: 'M26 88 L74 88 L74 78 L68 72 L68 42 L74 42 L74 16 L62 16 L62 26 L56 26 L56 16 L44 16 L44 26 L38 26 L38 16 L26 16 L26 42 L32 42 L32 72 L26 78 Z',
  bishop:
    'M28 88 L72 88 L72 80 L62 74 C74 60 70 44 56 32 A8 8 0 1 0 44 32 C30 44 26 60 38 74 L28 80 Z',
  knight:
    'M24 88 L76 88 L76 80 L70 74 L70 54 L74 38 L66 24 L58 14 L52 20 L44 12 L40 22 L30 32 L24 46 L32 54 L40 50 L42 58 L34 68 L30 76 L24 80 Z',
  queen:
    'M26 88 L74 88 L74 80 L64 74 L60 52 L76 22 L60 40 L50 14 L40 40 L24 22 L40 52 L36 74 L26 80 Z',
  king: 'M26 88 L74 88 L74 80 L64 74 L60 50 L60 42 L55 42 L55 32 L62 32 L62 24 L55 24 L55 12 L45 12 L45 24 L38 24 L38 32 L45 32 L45 42 L40 42 L40 50 L36 74 L26 80 Z',
  blocker:
    'M16 76 C16 52 34 40 50 40 C66 40 84 52 84 76 Z M30 46 A9 9 0 1 1 48 46 A9 9 0 1 1 30 46 Z M52 46 A9 9 0 1 1 70 46 A9 9 0 1 1 52 46 Z',
  dormant: 'M50 14 C70 14 78 44 78 60 C78 76 66 88 50 88 C34 88 22 76 22 60 C22 44 30 14 50 14 Z',
};

/**
 * The box each silhouette fills, in the units of `GLYPH_BOX`: its left and
 * right edges, its top, and its foot. A mark that points at a piece reads it,
 * to start and stop clear of the shape. A pawn is short, so a mark may come
 * closer to its square from above.
 */
export const GLYPH_BOUNDS: Record<
  PieceKind,
  { left: number; right: number; top: number; bottom: number }
> = {
  pawn: { left: 30, right: 70, top: 34, bottom: 86 },
  rook: { left: 26, right: 74, top: 16, bottom: 88 },
  bishop: { left: 28, right: 72, top: 24, bottom: 88 },
  knight: { left: 24, right: 76, top: 12, bottom: 88 },
  queen: { left: 24, right: 76, top: 14, bottom: 88 },
  king: { left: 26, right: 74, top: 12, bottom: 88 },
  blocker: { left: 16, right: 84, top: 37, bottom: 76 },
  dormant: { left: 22, right: 78, top: 14, bottom: 88 },
};

/** A piece of this kind shows its caster on its shape. Any other kind shows it as a badge. */
export const wearsCaster = (kind: PieceKind): boolean => kind === 'pawn';

/** The box of a piece. The head of a pawn that wears its caster is taller than a plain head. */
export const glyphBounds = (
  kind: PieceKind,
  casterId: CasterId | null,
): { left: number; right: number; top: number; bottom: number } =>
  casterId !== null && wearsCaster(kind)
    ? { ...GLYPH_BOUNDS[kind], top: CASTER_PAWN_TOP }
    : GLYPH_BOUNDS[kind];

/** Ink details drawn over the body. */
const DETAILS: Partial<Record<PieceKind, string>> = {
  rook: 'M44 60 L56 60 L56 72 L44 72 Z',
  bishop: 'M50 40 L54 56 L50 62 L46 56 Z',
  knight: 'M52 30 A4 4 0 1 1 52 38 A4 4 0 1 1 52 30 Z M38 46 L46 48 L38 52 Z',
  king: 'M46 56 L54 56 L54 70 L46 70 Z',
  queen: 'M44 58 L56 58 L52 70 L48 70 Z',
  blocker:
    'M36 44 A3.5 3.5 0 1 1 43 44 A3.5 3.5 0 1 1 36 44 Z M57 44 A3.5 3.5 0 1 1 64 44 A3.5 3.5 0 1 1 57 44 Z M36 62 Q50 72 64 62 Q50 68 36 62 Z',
  dormant: 'M36 50 L42 44 L48 50 L54 44 L60 50 L66 44 M34 64 L40 58 L46 64 L52 58 L58 64 L64 58',
};

const SHADOW_OFFSET = 4;

/**
 * The energy slots of a piece: one pill down its middle, cut into a cell for
 * each point of room. The cells are flush, so the end cells are round and a
 * cell between them is square. Garrett, 2026-10-13: "can they just be flush,
 * so the middle one is a quare and the top two are round ... will look like a
 * pill then". They were three discs with room between them.
 */
const SLOT_COLUMN_X = GLYPH_BOX / 2;
/** The foot of the pill is this far over the foot of the piece. On a rook the pill is centered on the body under the notches. */
const PILL_ABOVE_FOOT = 10;
const PILL_WIDTH = 14;
const CELL_HEIGHT = 14;
const SLOT_STAR_RADIUS = 5.2;
/** The ink between the floor of an empty cell and the edge of the pill. */
const FLOOR_EDGE = 1.6;
/** The ink between the floors of two empty cells. */
const FLOOR_DIVIDER = 1.6;
/** More ink over the top of a floor: the shadow of the rim, so the cell reads as a hole. */
const FLOOR_RIM = 1.4;

/** The energy a piece holds and the most it can hold. */
export type EnergyStore = { energy: number; cap: number };

/** The floor of an empty cell from `top` to `bottom`. An end of the pill is round. */
const floorPath = (
  top: number,
  bottom: number,
  roundTop: boolean,
  roundBottom: boolean,
): string => {
  const half = PILL_WIDTH / 2 - FLOOR_EDGE;
  const left = SLOT_COLUMN_X - half;
  const right = SLOT_COLUMN_X + half;
  const floorTop = top + (roundTop ? FLOOR_EDGE : FLOOR_DIVIDER / 2) + FLOOR_RIM;
  const floorBottom = bottom - (roundBottom ? FLOOR_EDGE : FLOOR_DIVIDER / 2);
  const head = roundTop
    ? `M${left} ${floorTop + half}A${half} ${half} 0 0 1 ${right} ${floorTop + half}`
    : `M${left} ${floorTop}L${right} ${floorTop}`;
  const foot = roundBottom
    ? `L${right} ${floorBottom - half}A${half} ${half} 0 0 1 ${left} ${floorBottom - half}Z`
    : `L${right} ${floorBottom}L${left} ${floorBottom}Z`;
  return `${head}${foot}`;
};

/**
 * The pill is the ink of the side. The cells fill from the foot up. A cell
 * with energy is ink with the star of energy in the color of the side. An
 * empty cell has no star. It is a hole: a floor in a darker tone of the side,
 * and the ink left over its top edge is the shadow of the rim.
 */
const energySlots = (kind: PieceKind, store: EnergyStore): SVGElement[] => {
  const height = store.cap * CELL_HEIGHT;
  const foot = GLYPH_BOUNDS[kind].bottom - PILL_ABOVE_FOOT;
  const cells = Array.from({ length: store.cap }, (_, place) => {
    const bottom = foot - place * CELL_HEIGHT;
    const top = bottom - CELL_HEIGHT;
    return place < store.energy
      ? svg('path', {
          class: 'glyph__socket-star',
          d: energyStarPath(SLOT_STAR_RADIUS, 'small', SLOT_COLUMN_X, top + CELL_HEIGHT / 2),
        })
      : svg('path', {
          class: 'glyph__socket-floor',
          d: floorPath(top, bottom, place === store.cap - 1, place === 0),
        });
  });
  return [
    svg('rect', {
      class: 'glyph__socket',
      x: SLOT_COLUMN_X - PILL_WIDTH / 2,
      y: foot - height,
      width: PILL_WIDTH,
      height,
      rx: PILL_WIDTH / 2,
    }),
    ...cells,
  ];
};

export const ownerClass = (owner: Owner): string => `glyph--${owner}`;

/**
 * Full size glyph for the board. The group is 100 by 100. Two copies of the
 * outline lie over the body with no fill. The style sheet fills one with dither
 * while the piece is stopped, and the other while it is uncapturable. A royal piece gets one more copy on top, filled
 * with dither. The style sheet shows one band of it at a time, so the piece
 * glints every few seconds. A piece with room for energy has its slots in
 * place of its ink detail. A pawn that carries a caster takes the outline of
 * the pawn of that caster, with the head of its sign, and wears the motif
 * under the covers, so a stop covers it. A piece of another kind that carries
 * a caster has no drawing for it, and keeps its badge.
 */
export const createPieceGlyph = (
  kind: PieceKind,
  owner: Owner,
  royal: boolean,
  store: EnergyStore | null = null,
  casterId: CasterId | null = null,
): SVGGElement => {
  const wearer = casterId !== null && wearsCaster(kind) ? casterId : null;
  const silhouette = wearer === null ? SILHOUETTES[kind] : casterPawnOutline(wearer);
  const slots = store && store.cap > 0 ? energySlots(kind, store) : [];
  const wear = wearer === null ? [] : casterPawnWear(wearer);
  const detail = slots.length > 0 || wear.length > 0 ? undefined : DETAILS[kind];
  return svg('g', { class: `glyph glyph--kind-${kind} ${ownerClass(owner)}` }, [
    svg('path', {
      class: 'glyph__shadow',
      d: silhouette,
      transform: `translate(${SHADOW_OFFSET} ${SHADOW_OFFSET})`,
    }),
    svg('path', { class: 'glyph__body', d: silhouette }),
    ...wear,
    ...slots,
    svg('path', { class: 'glyph__stopped', d: silhouette }),
    svg('path', { class: 'glyph__uncapturable', d: silhouette }),
    ...(detail ? [svg('path', { class: 'glyph__detail', d: detail })] : []),
    ...(royal ? [svg('path', { class: 'glyph__glint', d: silhouette })] : []),
  ]);
};

/** Small glyph for badges and the graveyard. Caller scales it. */
export const createMiniGlyph = (kind: PieceKind, owner: Owner): SVGGElement =>
  svg('g', { class: `glyph glyph--mini ${ownerClass(owner)}` }, [
    svg('path', { class: 'glyph__body', d: SILHOUETTES[kind] }),
  ]);
